import { z } from "zod";
import {
  CheckResult,
  CheckSeverity,
  Decision,
  IsoDateTime,
  OperatorEnvironment,
  PlayerRef,
} from "../common.ts";
import { CheckCause, LocationSourceUnavailableCause } from "./causes.ts";
import {
  Cursor,
  DEFAULT_TRANSACTION_LIMIT,
  MAX_TRANSACTION_LIMIT,
  OperatorFilter,
  PeriodBound,
  PeriodV1,
  repeatable,
  TransactionItemV1,
} from "./common.ts";

// Transactions of a player, a device or an IP (specs explorer-user-transactions-map,
// explorer-device-profile, explorer-home-search "Transactions by IP") and the transaction detail
// (spec explorer-transaction-detail); `explorer.read`, precise coordinates only with
// `location.precise.read` (audited `precise_location_viewed`).

const transactionFilters = {
  from: PeriodBound.optional(),
  to: PeriodBound.optional(),
  /** "Show approved" off = `deny`. */
  decision: Decision.optional(),
  cursor: Cursor.optional(),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(MAX_TRANSACTION_LIMIT)
    .default(DEFAULT_TRANSACTION_LIMIT),
};

/** `GET /users/{ref}/transactions`, `GET /devices/{ref}/transactions` (newest first). */
export const SubjectTransactionsQueryV1 = z
  .object({ ...transactionFilters, operator_id: z.uuid().optional() })
  .strict()
  .meta({ id: "SubjectTransactionsQueryV1" });
export type SubjectTransactionsQueryV1 = z.infer<typeof SubjectTransactionsQueryV1>;

export const TransactionListResponseV1 = z
  .object({
    period: PeriodV1,
    items: z.array(TransactionItemV1),
    /** Rows of this page without a position (located by IP country only). */
    no_position: z.int().nonnegative(),
    next_cursor: Cursor.nullable(),
  })
  .strict()
  .meta({ id: "TransactionListResponseV1" });
export type TransactionListResponseV1 = z.infer<typeof TransactionListResponseV1>;

/** `GET /ips/{ip}/transactions`: only the last 180 days (the IP is kept 180 days). */
export const IpTransactionsQueryV1 = z
  .object({ ...transactionFilters, operator_id: OperatorFilter.optional() })
  .strict()
  .meta({ id: "IpTransactionsQueryV1" });
export type IpTransactionsQueryV1 = z.infer<typeof IpTransactionsQueryV1>;

export const IpTransactionsResponseV1 = z
  .object({
    ...TransactionListResponseV1.shape,
    ip: z.string(),
    /** Distinct `player_ref` and `device_ref` seen on the IP in the period ("12 players, N devices"). */
    distinct_players: z.int().nonnegative(),
    distinct_devices: z.int().nonnegative(),
  })
  .strict()
  .meta({ id: "IpTransactionsResponseV1" });
export type IpTransactionsResponseV1 = z.infer<typeof IpTransactionsResponseV1>;

/** No query parameter: the installation's environment is implied (a leftover `environment` is refused). */
export const TransactionDetailQueryV1 = z
  .object({})
  .strict()
  .meta({ id: "TransactionDetailQueryV1" });
export type TransactionDetailQueryV1 = z.infer<typeof TransactionDetailQueryV1>;

const Neighbour = z
  .object({ verification_id: z.uuid(), decision: Decision, occurred_at: IsoDateTime })
  .strict();

/** Coordinates of a location source: shown only with `location.precise.read` within retention. */
export const SourceCoordinatesV1 = z
  .object({
    status: z.enum(["precise", "redacted", "expired", "not_applicable"]),
    latitude: z.number().min(-90).max(90).optional(),
    longitude: z.number().min(-180).max(180).optional(),
    accuracy_m: z.number().nonnegative().optional(),
  })
  .strict()
  .meta({ id: "SourceCoordinatesV1" });

export const LocationSourceV1 = z
  .object({
    kind: z.enum(["ip", "browser", "wifi"]),
    availability: z.enum(["available", "unavailable"]),
    /** A `LocationSourceUnavailableCause`; any other string is a cause newer than this contract. */
    unavailable_cause: LocationSourceUnavailableCause.or(z.string()).nullable(),
    is_primary: z.boolean(),
    country: z.string().nullable(),
    subdivision: z.string().nullable(),
    /** IBGE code of the municipality (kept for search and exports). */
    municipality: z.string().nullable(),
    /** Name of the municipality (e.g. "Rio de Janeiro"); null when unknown: show the code. */
    municipality_name: z.string().nullable(),
    geohash5: z.string().nullable(),
    accuracy_km: z.number().nonnegative().nullable(),
    border_uncertain: z.boolean().nullable(),
    /** Absent for the IP source in v0 (no position); drawn on the map only when a geohash exists. */
    coordinates: SourceCoordinatesV1,
  })
  .strict()
  .meta({ id: "LocationSourceV1" });

export const IntegrityV1 = z
  .object({
    /** `unavailable`: no signed result was stored for this (older) transaction. */
    status: z.enum(["verified", "signature_invalid", "mismatch", "unavailable"]),
    /** Fields that differ from the signed result (`mismatch`). */
    fields: z.array(z.string()).optional(),
  })
  .strict()
  .meta({ id: "IntegrityV1" });

export const TransactionDetailResponseV1 = z
  .object({
    verification_id: z.uuid(),
    operator_id: z.uuid(),
    environment: OperatorEnvironment,
    decision: Decision,
    occurred_at: IsoDateTime,
    player: z
      .object({
        player_ref: PlayerRef,
        transaction_count: z.int().nonnegative(),
        /** Status at the time of the transaction; null = no information. */
        status_at_transaction: z.enum(["none", "blocked"]).nullable(),
      })
      .strict(),
    device: z
      .object({
        device_ref: z.uuid(),
        transaction_count: z.int().nonnegative(),
        status_at_transaction: z.enum(["none", "blocked"]).nullable(),
      })
      .strict()
      .nullable(),
    previous: Neighbour.nullable(),
    next: Neighbour.nullable(),
    /** Label-value data; `null` = "No information" (always for ISP, organization, connection type, postal code in v0). */
    data: z
      .object({
        response_time_ms: z.int().nonnegative().nullable(),
        sdk: z.object({ name: z.string(), version: z.string() }).strict(),
        product: z.string(),
        purpose: z.string(),
        valid_until: IsoDateTime.nullable(),
        next_check_in: z.int().nullable(),
        buffer_time: z.int().nullable(),
        ip: z.string().nullable(),
        ip_country: z.string().nullable(),
        connection_type: z.string().nullable(),
        isp: z.string().nullable(),
        ip_organization: z.string().nullable(),
        postal_code: z.string().nullable(),
        geohash5: z.string().nullable(),
        ip_dataset_version: z.string().nullable(),
        boundary_dataset_version: z.string().nullable(),
        location_permission: z.string(),
        /** Browser-unavailable fields shown as "No information". */
        not_collected: z.array(z.enum(["mac_address", "wifi_ssid", "wifi_bssid"])),
      })
      .strict(),
    reasons: z.array(z.object({ code: z.string(), category: z.string() }).strict()),
    checks: z.array(
      z
        .object({
          code: z.string(),
          severity: CheckSeverity.or(z.string()),
          required: z.boolean(),
          result: CheckResult,
          reason: z.string().nullable(),
          /** A `CheckCause` or a Reason code; any other string is newer than this contract. */
          cause: CheckCause.or(z.string()).nullable(),
        })
        .strict(),
    ),
    sources: z.array(LocationSourceV1),
    /** Map notice: located by IP country only, or approximate location after retention. */
    location_notice: z.enum(["located_by_ip_country_only", "approximate_location"]).nullable(),
    /** "Device information" tab; null after the 180-day tier. */
    device_info: z
      .object({
        timezone: z.string().nullable(),
        os: z.string().nullable(),
        os_version: z.string().nullable(),
        screen_resolution: z.string().nullable(),
        browser: z.string().nullable(),
        browser_version: z.string().nullable(),
        language: z.string().nullable(),
      })
      .strict()
      .nullable(),
    /** "Raw response" tab: the signed result read from core on demand, and the normalized data. */
    raw_response: z
      .object({
        verdict_token: z.string().nullable(),
        normalized: z.record(z.string(), z.unknown()),
      })
      .strict(),
    integrity: IntegrityV1,
  })
  .strict()
  .meta({ id: "TransactionDetailResponseV1" });
export type TransactionDetailResponseV1 = z.infer<typeof TransactionDetailResponseV1>;

/** At most this many ids per `GET /transactions/locations`. */
export const MAX_LOCATION_IDS = 200;

/** `GET /transactions/locations`: precise coordinates on demand (`location.precise.read`, audited). */
export const TransactionLocationsQueryV1 = z
  .object({
    operator_id: z.uuid(),
    id: repeatable(z.uuid(), MAX_LOCATION_IDS),
  })
  .strict()
  .meta({ id: "TransactionLocationsQueryV1" });
export type TransactionLocationsQueryV1 = z.infer<typeof TransactionLocationsQueryV1>;

export const TransactionLocationsResponseV1 = z
  .object({
    locations: z.array(
      z
        .object({
          verification_id: z.uuid(),
          latitude: z.number().min(-90).max(90),
          longitude: z.number().min(-180).max(180),
          accuracy_m: z.number().nonnegative(),
        })
        .strict(),
    ),
    /** Ids whose coordinates no longer exist (retention): shown as "approximate location". */
    expired: z.array(z.uuid()),
  })
  .strict()
  .meta({ id: "TransactionLocationsResponseV1" });
export type TransactionLocationsResponseV1 = z.infer<typeof TransactionLocationsResponseV1>;
